// client/src/components/categorias/Categorias.js - VERSÃO CORRIGIDA
import React, { Fragment, useState, useMemo, useCallback } from 'react';
import { useCategorias, useMutateCategorias } from '../../hooks/useCategorias';
import CategoriaForm from './CategoriaForm';
import CategoriaRow from './CategoriaRow';
import FiltroCategorias from './FiltroCategorias';
import { PlusCircle, Filter as FilterIcon, Loader2, Tag, XCircle, AlertCircle, Eye, Trash2, RefreshCw } from 'lucide-react';
import Pagination from '../common/Pagination';
import { useMe } from '../../hooks/useAuth';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const Categorias = () => {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [currentCategoria, setCurrentCategoria] = useState(null);
    const [selectedCategoria, setSelectedCategoria] = useState(null);
    const categoriasPerPage = 50;

    // ✅ CORREÇÃO: Usar useMe() para obter accountId
    const { accountId, isLoading: userLoading } = useMe();

    const queryParams = useMemo(() => ({
        page: currentPage, 
        limit: categoriasPerPage, 
        ...activeFilters
    }), [currentPage, activeFilters, categoriasPerPage]);

    // ✅ CORREÇÃO: Passar accountId para o hook
    const { 
        data: categoriasData, 
        isLoading, 
        error,
        refetch 
    } = useCategorias(accountId, queryParams);
    
    const { deleteCategoria, isPending } = useMutateCategorias(accountId);

    // ✅ CORREÇÃO: Tratamento seguro dos dados
    const categorias = categoriasData?.data || [];
    const totalCategorias = categoriasData?.totalCount || categoriasData?.count || 0;
    
    const handleApplyFilters = useCallback((filters) => { 
        setCurrentPage(1); 
        setActiveFilters(filters); 
    }, []);

    const handleAdicionarClick = useCallback(() => { 
        setCurrentCategoria(null); 
        setIsFormVisible(true); 
    }, []);

    const handleCloseForm = useCallback(() => { 
        setIsFormVisible(false); 
        setCurrentCategoria(null); 
    }, []);

    const handleDelete = useCallback((categoriaId) => {
        if (!categoriaId) return;
        if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
            deleteCategoria.mutate(categoriaId, {
                onSuccess: () => setSelectedCategoria(null),
            });
        }
    }, [deleteCategoria]);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForm();
        refetch();
    }, [handleCloseForm, refetch]);

    const handleEditClick = useCallback((categoria) => {
        if (!categoria || !categoria._id) return;
        setCurrentCategoria(categoria);
        setIsFormVisible(true);
        setSelectedCategoria(null);
    }, []);

    // ✅ CORREÇÃO: Loading state aprimorado
    if (userLoading) {
        return (
            <div className="page-management-container">
                <div className="page-header">
                    <h1 className="page-title">
                        <Tag className="icon-title"/>
                        Gerenciamento de Categorias
                    </h1>
                </div>
                <div className="table-loading-state">
                    <Loader2 size={24} className="animate-spin" />
                    <span>Carregando informações do usuário...</span>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Tag />}
                    title="Gerenciamento de Categorias"
                    actions={<>
                        {!isFormVisible && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoading}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {isFormVisible && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {/* ✅ EXIBIR ERRO SE HOUVER */}
                {error && !isLoading && (
                    <div className="content-panel">
                        <div className="alert alert-danger">
                            <AlertCircle size={20} className="icon" />
                            <div>
                                <strong>Erro ao carregar categorias:</strong>
                                <p>{error.message}</p>
                                {/* .btn/.btn-sm/.btn-outline nao tem CSS em lugar
                                    nenhum do projeto — renderizava sem estilo */}
                                <button
                                    onClick={() => refetch()}
                                    className="action-button action-button-primary"
                                >
                                    <RefreshCw size={18} className="icon" />
                                    Tentar Novamente
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {isFormVisible && (
                    <div className="content-panel">
                        <CategoriaForm 
                            key={currentCategoria?._id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            current={currentCategoria}
                        />
                    </div>
                )}

                {showFilters && !isFormVisible && (
                    <div className="content-panel">
                        <div className="panel-header">
                            <h3 className="panel-title">Filtros</h3>
                            {Object.keys(activeFilters).length > 0 && (
                                <button 
                                    onClick={() => handleApplyFilters({})}
                                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                                >
                                    Limpar Filtros
                                </button>
                            )}
                        </div>
                        <FiltroCategorias 
                            onApplyFilters={handleApplyFilters} 
                            loading={isLoading}
                            activeFilters={activeFilters}
                        />
                    </div>
                )}
            </div>

            {!isFormVisible && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando categorias...</span>
                            </div>
                        ) : categorias.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Tipo</th>
                                        <th>Pai</th>
                                        <th>Status</th>
                                        <th>Itens</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categorias.map((categoria, index) => (
                                        <CategoriaRow
                                            key={categoria._id || `categoria-${index}`}
                                            categoria={categoria}
                                            onOpenActions={setSelectedCategoria}
                                            loading={isPending}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Tag size={48} />
                                <h3>Nenhuma categoria encontrada</h3>
                                <p>
                                    {Object.keys(activeFilters).length > 0
                                        ? 'Nenhuma categoria encontrada com os filtros aplicados.'
                                        : 'Clique em "Adicionar Categoria" para adicionar a primeira.'}
                                </p>
                                {Object.keys(activeFilters).length > 0 && (
                                    <button onClick={() => handleApplyFilters({})} className="action-button action-button-secondary">
                                        Limpar Filtros
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <Pagination
                        itemsPerPage={categoriasPerPage}
                        totalItems={totalCategorias}
                        onPageChange={setCurrentPage}
                        currentPage={currentPage}
                    />
                </div>
            )}

            <FabButton
                onClick={handleAdicionarClick}
                title="Adicionar Categoria"
                visible={!isFormVisible}
                disabled={isLoading}
                icon={<Tag size={26} className="icon"/>}
            />

            <ActionsDrawer
                open={!!selectedCategoria}
                onClose={() => setSelectedCategoria(null)}
                title={selectedCategoria?.nome || 'Categoria'}
                actions={selectedCategoria ? [
                    {
                        key: 'editar',
                        label: 'Visualizar/Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedCategoria),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedCategoria._id),
                        destructive: true,
                        loading: isPending,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default Categorias;